import React, { useEffect, useMemo, useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import Swal from 'sweetalert2';
import axiosInstance from '../../utils/axiosInstance';

const EMPTY_FORM = {
  name: '',
  color: '#6366f1',
  sortOrder: 0,
  isActive: true,
};

const normalizeEventType = (eventType) => ({
  ...eventType,
  eventTypeId: Number(eventType?.eventTypeId),
  name: String(eventType?.name || '').trim(),
  color: /^#[0-9a-fA-F]{6}$/.test(String(eventType?.color || '').trim())
    ? String(eventType.color).trim()
    : '#6366f1',
  sortOrder: Number.isFinite(Number(eventType?.sortOrder)) ? Number(eventType.sortOrder) : 0,
  isActive: Boolean(Number(eventType?.isActive ?? 0)),
});

const toSoftColor = (color, alpha = 0.14) => {
  const raw = String(color || '').trim();
  const hex = raw.startsWith('#') ? raw.slice(1) : raw;
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return '#eef2ff';
  const red = parseInt(hex.slice(0, 2), 16);
  const green = parseInt(hex.slice(2, 4), 16);
  const blue = parseInt(hex.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

export default function OtherEventTypes() {
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingTypeId, setEditingTypeId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  const loadEventTypes = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const response = await axiosInstance.get('/admin/other-events/event-types');
      const list = Array.isArray(response?.data?.eventTypes) ? response.data.eventTypes : [];
      setEventTypes(list.map(normalizeEventType));
    } catch (error) {
      console.error('Failed to load event types', error);
      Swal.fire({
        icon: 'error',
        title: 'Load failed',
        text: error?.response?.data?.message || 'Could not load other event types.',
        confirmButtonColor: '#4f46e5',
      });
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    loadEventTypes();
  }, []);

  const stats = useMemo(() => {
    const activeCount = eventTypes.filter((eventType) => eventType.isActive).length;
    return {
      total: eventTypes.length,
      active: activeCount,
      inactive: eventTypes.length - activeCount,
    };
  }, [eventTypes]);

  const filteredEventTypes = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return eventTypes.filter((eventType) => {
      const matchesQuery =
        !query ||
        eventType.name.toLowerCase().includes(query) ||
        String(eventType.sortOrder).includes(query) ||
        eventType.color.toLowerCase().includes(query);

      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && eventType.isActive) ||
        (statusFilter === 'inactive' && !eventType.isActive);

      return matchesQuery && matchesStatus;
    });
  }, [eventTypes, searchQuery, statusFilter]);

  const resetForm = () => {
    setEditingTypeId(null);
    setFormData(EMPTY_FORM);
    setErrors({});
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validateForm = () => {
    const nextErrors = {};
    const trimmedName = String(formData.name || '').trim();
    const normalizedName = trimmedName.toLowerCase();

    if (!trimmedName) {
      nextErrors.name = 'Name is required.';
    } else if (trimmedName.length > 120) {
      nextErrors.name = 'Name must be 120 characters or fewer.';
    }

    const hasDuplicateName = eventTypes.some((eventType) => {
      if (editingTypeId && eventType.eventTypeId === editingTypeId) return false;
      return eventType.name.toLowerCase() === normalizedName;
    });
    if (!nextErrors.name && hasDuplicateName) {
      nextErrors.name = 'An event type with this name already exists.';
    }

    if (!/^#[0-9a-fA-F]{6}$/.test(String(formData.color || '').trim())) {
      nextErrors.color = 'Pick a valid 6-digit color.';
    }

    if (!Number.isInteger(Number(formData.sortOrder))) {
      nextErrors.sortOrder = 'Sort order must be a whole number.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleEdit = (eventType) => {
    setEditingTypeId(eventType.eventTypeId);
    setFormData({
      name: eventType.name,
      color: eventType.color,
      sortOrder: eventType.sortOrder,
      isActive: eventType.isActive,
    });
    setErrors({});
  };

  const handleStatusAction = async (eventType) => {
    const isActive = Boolean(eventType.isActive);
    const result = await Swal.fire({
      icon: 'warning',
      title: isActive ? 'Deactivate event type?' : 'Restore event type?',
      text: isActive
        ? 'This type will stop appearing for new Other Events until you reactivate it.'
        : 'This type will become available for new Other Events again.',
      showCancelButton: true,
      confirmButtonColor: isActive ? '#dc2626' : '#16a34a',
      cancelButtonColor: '#6b7280',
      confirmButtonText: isActive ? 'Yes, deactivate' : 'Yes, restore',
    });

    if (!result.isConfirmed) return;

    try {
      if (isActive) {
        await axiosInstance.delete(`/admin/other-events/event-types/${eventType.eventTypeId}`);
        setEventTypes((prev) =>
          prev.map((item) =>
            item.eventTypeId === eventType.eventTypeId ? { ...item, isActive: false } : item
          )
        );
      } else {
        const response = await axiosInstance.put(
          `/admin/other-events/event-types/${eventType.eventTypeId}`,
          { isActive: true }
        );
        const restored = normalizeEventType(response?.data?.eventType || { ...eventType, isActive: true });
        setEventTypes((prev) =>
          prev.map((item) => (item.eventTypeId === eventType.eventTypeId ? restored : item))
        );
      }

      if (editingTypeId === eventType.eventTypeId) {
        setFormData((prev) => ({ ...prev, isActive: !isActive }));
      }

      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: isActive ? 'Event type deactivated' : 'Event type restored',
        showConfirmButton: false,
        timer: 1800,
        timerProgressBar: true,
      });
    } catch (error) {
      console.error('Failed to update event type status', error);
      Swal.fire({
        icon: 'error',
        title: 'Update failed',
        text: error?.response?.data?.message || 'Could not update event type status.',
        confirmButtonColor: '#4f46e5',
      });
    }
  };

  const [modalOpen, setModalOpen] = useState(false);

  const openAddModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditModal = (eventType) => {
    handleEdit(eventType);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    resetForm();
  };

  const handleSubmitAndClose = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    const payload = {
      name: String(formData.name || '').trim(),
      color: String(formData.color || '').trim(),
      sortOrder: Number(formData.sortOrder) || 0,
      isActive: Boolean(formData.isActive),
    };

    setSaving(true);
    try {
      const response = editingTypeId
        ? await axiosInstance.put(`/admin/other-events/event-types/${editingTypeId}`, payload)
        : await axiosInstance.post('/admin/other-events/event-types', payload);

      const saved = normalizeEventType(response?.data?.eventType || payload);
      setEventTypes((prev) => {
        if (editingTypeId) {
          return prev.map((et) => (et.eventTypeId === editingTypeId ? saved : et));
        }
        return [saved, ...prev];
      });

      closeModal();
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: editingTypeId ? 'Event type updated' : 'Event type created',
        showConfirmButton: false,
        timer: 1800,
        timerProgressBar: true,
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Save failed',
        text: error?.response?.data?.message || 'Could not save the event type.',
        confirmButtonColor: '#4f46e5',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout title="Other Event Types">
      <div className="p-6">

        {/* Page header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Other Event Types</h1>
            <p className="mt-1 text-gray-600">Manage the event-type labels used in the Other Events flow.</p>
          </div>
          <button
            type="button"
            onClick={openAddModal}
            className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 transition-colors"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Event Type
          </button>
        </div>

        {/* Stat cards */}
        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="rounded-lg bg-white p-6 shadow">
            <p className="text-sm text-gray-600">Total Types</p>
            <p className="text-3xl font-bold text-indigo-600">{stats.total}</p>
          </div>
          <div className="rounded-lg bg-white p-6 shadow">
            <p className="text-sm text-gray-600">Active</p>
            <p className="text-3xl font-bold text-green-600">{stats.active}</p>
          </div>
          <div className="rounded-lg bg-white p-6 shadow">
            <p className="text-sm text-gray-600">Inactive</p>
            <p className="text-3xl font-bold text-amber-500">{stats.inactive}</p>
          </div>
        </div>

        {/* Search + filter */}
        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="relative md:col-span-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or color…"
              className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            />
            <svg className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
          >
            <option value="all">All types</option>
            <option value="active">Active only</option>
            <option value="inactive">Inactive only</option>
          </select>
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-lg bg-white shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">#</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Color</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Sort Order</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-6 py-4"><div className="h-4 w-6 rounded bg-gray-200" /></td>
                    <td className="px-6 py-4"><div className="h-4 w-32 rounded bg-gray-200" /></td>
                    <td className="px-6 py-4"><div className="h-6 w-16 rounded bg-gray-200" /></td>
                    <td className="px-6 py-4"><div className="h-4 w-10 rounded bg-gray-200" /></td>
                    <td className="px-6 py-4"><div className="h-5 w-14 rounded-full bg-gray-200" /></td>
                    <td className="px-6 py-4"><div className="mx-auto h-4 w-24 rounded bg-gray-200" /></td>
                  </tr>
                ))
              ) : filteredEventTypes.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-500">
                    No event types found.{' '}
                    {(searchQuery || statusFilter !== 'all') && (
                      <button
                        type="button"
                        onClick={() => { setSearchQuery(''); setStatusFilter('all'); }}
                        className="text-indigo-600 hover:underline"
                      >
                        Clear filters
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                filteredEventTypes.map((eventType, index) => (
                  <tr key={eventType.eventTypeId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">{index + 1}</td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{eventType.name}</td>
                    <td className="px-6 py-4">
                      <span
                        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold text-white"
                        style={{ backgroundColor: eventType.color }}
                      >
                        <span className="h-2 w-2 rounded-full bg-white/80" />
                        {eventType.color.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{eventType.sortOrder}</td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-block rounded-full px-2.5 py-1 text-xs font-semibold ${
                          eventType.isActive
                            ? 'bg-green-100 text-green-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}
                      >
                        {eventType.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center text-sm font-medium">
                      <button
                        type="button"
                        onClick={() => openEditModal(eventType)}
                        className="mr-3 text-indigo-600 hover:text-indigo-900"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleStatusAction(eventType)}
                        className={eventType.isActive ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}
                      >
                        {eventType.isActive ? 'Deactivate' : 'Restore'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Add / Edit Modal */}
        {modalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
              <div className="sticky top-0 rounded-t-lg border-b border-gray-200 bg-white px-6 py-4">
                <h2 className="text-xl font-bold text-gray-800">
                  {editingTypeId ? 'Edit Event Type' : 'Add Event Type'}
                </h2>
              </div>

              <form onSubmit={handleSubmitAndClose}>
                <div className="space-y-4 px-6 py-5">

                  {/* Live preview */}
                  <div
                    className="rounded-lg border p-3"
                    style={{ backgroundColor: toSoftColor(formData.color, 0.12), borderColor: `${formData.color}33` }}
                  >
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Preview</p>
                    <span
                      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold text-white"
                      style={{ backgroundColor: formData.color }}
                    >
                      <span className="h-2 w-2 rounded-full bg-white/80" />
                      {formData.name.trim() || 'Event type name'}
                    </span>
                  </div>

                  {/* Name */}
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                      placeholder="e.g. Exam supervising"
                      maxLength={120}
                      className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                        errors.name
                          ? 'border-red-400 focus:ring-red-200'
                          : 'border-gray-300 focus:border-indigo-500 focus:ring-indigo-200'
                      }`}
                    />
                    {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
                  </div>

                  {/* Color + Sort Order */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Color</label>
                      <div className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2">
                        <input
                          type="color"
                          value={formData.color}
                          onChange={(e) => handleFormChange('color', e.target.value)}
                          className="h-8 w-10 cursor-pointer rounded border-0 bg-transparent p-0"
                        />
                        <input
                          type="text"
                          value={formData.color}
                          onChange={(e) => handleFormChange('color', e.target.value)}
                          maxLength={7}
                          className="w-full border-0 bg-transparent text-sm font-semibold uppercase text-gray-700 focus:outline-none"
                        />
                      </div>
                      {errors.color && <p className="mt-1 text-xs text-red-500">{errors.color}</p>}
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Sort Order</label>
                      <input
                        type="number"
                        step="1"
                        value={formData.sortOrder}
                        onChange={(e) => handleFormChange('sortOrder', e.target.value)}
                        className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                          errors.sortOrder
                            ? 'border-red-400 focus:ring-red-200'
                            : 'border-gray-300 focus:border-indigo-500 focus:ring-indigo-200'
                        }`}
                      />
                      {errors.sortOrder && <p className="mt-1 text-xs text-red-500">{errors.sortOrder}</p>}
                    </div>
                  </div>

                  {/* Active toggle */}
                  <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={Boolean(formData.isActive)}
                      onChange={(e) => handleFormChange('isActive', e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span>
                      <span className="block text-sm font-semibold text-gray-800">Available for new events</span>
                      <span className="block text-xs text-gray-500">
                        Uncheck to hide this type from new event selections while preserving history.
                      </span>
                    </span>
                  </label>
                </div>

                <div className="sticky bottom-0 flex justify-end gap-2 rounded-b-lg border-t border-gray-200 bg-white px-6 py-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    disabled={saving}
                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {saving ? 'Saving…' : editingTypeId ? 'Save Changes' : 'Create Event Type'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
}
